import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { asyncActions } from './asyncActions';
import { EmailModel } from '@/Models/EmailModel';
import { Toasts } from '@/Components/Toast/Toast';
import { stringUtils } from '@/Utils';
import { localizationUtils } from '@/Utils/localizationUtils';
import { EmailTemplateRow } from '@/Types/EmailTemplateRow';
import { DocumentInfoForEmailModel } from '@/Models/DocumentInfoModel';
import { Nullable } from '@/Types';
import { NumberGenerationType } from '@/Enums';
import { documentTemplateMapping } from '@/Mapping/DocumentTemplateMapping';

export enum SendEmailStep {
  None = 'None',
  CreateDocument = 'CreateDocument',
  SingleDocumentPrint = 'SingleDocumentPrint',
  SelectTemplate = 'SelectTemplate',
  SelectDocumentNumber = 'SelectDocumentNumber',
  EmailPreview = 'EmailPreview',
  TemplateNotExists = 'TemplateNotExists',
  Close = 'Close',
}

export type EmailSendModalState = {
  documentType: Nullable<NumberGenerationType>;
  currentStep: SendEmailStep;
  emailTemplates: EmailTemplateRow[];
  selectedEmailTemplate: EmailTemplateRow | null;
  selectedTypeNumbers: string[];
  documentNumbersInfo: DocumentInfoForEmailModel[];
  selectedNumbers: string[];
  emailPreviewModalData: Nullable<{ email: EmailModel; orderId: number }>;
};

const initialState: EmailSendModalState = {
  documentType: null,
  currentStep: SendEmailStep.None,
  emailTemplates: [],
  selectedEmailTemplate: null,
  selectedTypeNumbers: [],
  documentNumbersInfo: [],
  selectedNumbers: [],
  emailPreviewModalData: null,
};

const emailSendSlice = createSlice({
  name: 'emailSendModalSlice',
  initialState,
  reducers: {
    setDocumentType(state, action: PayloadAction<Nullable<NumberGenerationType>>) {
      state.documentType = action.payload;
    },
    setSendEmailStep(state, action: PayloadAction<SendEmailStep>) {
      state.currentStep = action.payload;
    },
    setSelectedEmailTemplate(state, action: PayloadAction<EmailTemplateRow | null>) {
      state.selectedEmailTemplate = action.payload;
    },
    setSelectedTypeNumbers(state, action: PayloadAction<string[]>) {
      state.selectedTypeNumbers = action.payload;
    },
    setSelectedDocumentNumbers(state, action: PayloadAction<string[]>) {
      state.selectedNumbers = action.payload;
    },
    setEmailPreviewModalData(state, action: PayloadAction<typeof initialState.emailPreviewModalData>) {
      state.emailPreviewModalData = action.payload;
    },
    setGenerateNumberSendEmail(state, action: PayloadAction<{ guid: string; sendEmail: boolean }>) {
      const { guid, sendEmail } = action.payload;

      const row = state.emailTemplates.find((x) => x.guid == guid);
      if (row) {
        row.generateNumberSendOrderEmail = sendEmail;
      }
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) =>
    builder
      .addCase(asyncActions.sendFormattedEmailForOrder.fulfilled, (state, action) => {
        const {
          result,
          translation: {
            ModalWindows: { EmailSendModal },
          },
          localization,
        } = action.payload;

        if (!result.errors) {
          const toastText = localizationUtils.insertTranslatedParamsToString(
            EmailSendModal.EmailsSuccesfullySent,
            [1],
            EmailSendModal.parts,
            localization,
          );

          Toasts.showSuccess({
            title: EmailSendModal.EmailSend,
            text: stringUtils.insertParamsToString(toastText, [1]),
          });

          state.currentStep = SendEmailStep.Close;
        } // if
      })
      .addCase(asyncActions.getEmailPreviewForOrder.fulfilled, (state, action) => {
        if (!action.payload.errors) {
          state.currentStep = SendEmailStep.EmailPreview;
          state.emailPreviewModalData = action.payload;
        } // if
      })
      .addCase(asyncActions.getDocumentsForEmail.fulfilled, (state, action) => {
        state.documentNumbersInfo = action.payload.models;
      })
      .addCase(asyncActions.getEmailTemplates.fulfilled, (state, action) => {
        state.emailTemplates = action.payload.templates.map((template) => ({
          name: template.name,
          guid: template.guid,
          previewBeforeSending: template.emailTemplateSettings?.previewBeforeSending,
          generateNumberSendOrderEmail: template.generatingNumberSettings?.sendOrderEmail,
          numberGenerationType: documentTemplateMapping.convertDocumentTypeToNumberGenerationType(
            template.automaticallyGeneratedDocumentType,
          ),
        }));
      })
      .addMatcher(
        (action) =>
          [asyncActions.sendEmailForOrders.fulfilled.type, asyncActions.sendEmailForOrder.fulfilled.type].includes(
            action.type,
          ),
        (state, action) => {
          const {
            result: { successEmailsCount },
            translation: {
              ModalWindows: { EmailSendModal },
            },
            localization,
          } = action.payload;

          if (action.payload.result.successEmailsCount) {
            const toastText = localizationUtils.insertTranslatedParamsToString(
              EmailSendModal.EmailsSuccesfullySent,
              [successEmailsCount],
              EmailSendModal.parts,
              localization,
            );

            Toasts.showSuccess({
              title: EmailSendModal.EmailSend,
              text: stringUtils.insertParamsToString(toastText, [successEmailsCount]),
            });
          } // if

          state.currentStep = SendEmailStep.Close;
        },
      ),
});

const { actions, reducer } = emailSendSlice;

export const emailSendModalReducer = reducer;
export const emailSendModalActions = actions;
export const emailSendModalAsyncActions = asyncActions;
